// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Communication } from '@medplum/fhirtypes';

/**
 * Pure helpers for working with notification Communication resources.
 *
 * Notifications are stored as FHIR Communication resources by vintasend-medplum.
 * This module exposes only renderer-agnostic logic — no Medplum client, no React.
 *
 * Tags applied by vintasend-medplum:
 *   - notificationType (e.g. "IN_APP" / "EMAIL" / "SMS")
 *   - contextName       (the notification key, e.g. "patient.system.appointment-created")
 *   - the literal "notification" marker
 */

/** Polaris-managed extension URL recording when a notification was read. */
export const NOTIFICATION_READ_AT_URL = 'http://polarissleep.com/fhir/StructureDefinition/notification-readAt';

/** vintasend channel tag value for in-app notifications. */
export const IN_APP_TAG = 'IN_APP';

/** vintasend marker tag applied to every notification Communication. */
export const NOTIFICATION_TAG = 'notification';

const VINTASEND_RESERVED_TAGS = new Set<string>([IN_APP_TAG, 'EMAIL', 'SMS', NOTIFICATION_TAG]);

/** True when the Communication has a Polaris readAt extension. */
export function isRead(communication: Communication): boolean {
  return communication.extension?.some((ext) => ext.url === NOTIFICATION_READ_AT_URL) ?? false;
}

/** Title (Communication.topic.text) with a sensible fallback. */
export function getNotificationTitle(communication: Communication): string {
  return communication.topic?.text?.trim() || 'Notification';
}

/** Body — first payload contentString. Empty string if none. */
export function getNotificationBody(communication: Communication): string {
  return communication.payload?.[0]?.contentString ?? '';
}

/** Send timestamp (Communication.sent), falling back to meta.lastUpdated. */
export function getNotificationSentAt(communication: Communication): Date | undefined {
  const stamp = communication.sent ?? communication.meta?.lastUpdated;
  return stamp ? new Date(stamp) : undefined;
}

/**
 * Extract the notification key (e.g. "patient.system.appointment-created") from
 * the Communication's meta tags. vintasend tags Communications with the channel
 * (IN_APP/EMAIL/SMS), the contextName (the key), and the literal "notification"
 * marker. The key is whichever tag isn't one of the reserved values.
 */
export function getNotificationKey(communication: Communication): string | undefined {
  return communication.meta?.tag?.find((t) => t.code && !VINTASEND_RESERVED_TAGS.has(t.code))?.code;
}

/**
 * Find the first `about` reference whose resource type matches `resourceType`,
 * and return the `id` portion (e.g. "abc-123" from "Appointment/abc-123"). Returns
 * undefined when no matching reference is present.
 */
export function getAboutId(communication: Communication, resourceType: string): string | undefined {
  const prefix = `${resourceType}/`;
  const ref = communication.about?.find((a) => a.reference?.startsWith(prefix))?.reference;
  return ref ? ref.slice(prefix.length) : undefined;
}
